// src/components/WalletSecurity/WalletLink.jsx
// "Conectar mi wallet" a una cuenta YA logueada (ej. usuario de email que quiere
// sumar su wallet). Conecta/crea la wallet con Privy, firma el mismo mensaje honesto
// y llama /api/auth/wallet/link (autenticado) → pega el walletAddress a la MISMA
// cuenta, sin perder username/reputación/historial. Al terminar, actualiza el user.
//
// Se monta solo con REACT_APP_PRIVY_APP_ID (lo decide el caller) y solo si el usuario
// todavía NO tiene wallet vinculada.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { FaWallet } from 'react-icons/fa';
import { FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import { useAuth } from '../../Context/AuthContext';
import { buildLoginMessage, humanizeWalletError } from '../../lib/useWalletLogin';
import './WalletLink.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function pickWallet(wallets) {
  if (!wallets || !wallets.length) return null;
  return wallets.find(w => w.walletClientType === 'privy') || wallets[0];
}

export default function WalletLink() {
  const { ready, authenticated, login: openPrivy } = usePrivy();
  const { wallets } = useWallets();
  const { user, updateUser } = useAuth();

  const [status, setStatus] = useState('idle'); // idle|connecting|signing|linking|done|error
  const [error, setError]   = useState('');
  const pending = useRef(false);

  const runLink = useCallback(async (wallet) => {
    try {
      const address = wallet.address.toLowerCase();
      setStatus('signing');
      const token = localStorage.getItem('token');
      const nres  = await fetch(`${API_URL}/api/auth/wallet/nonce`);
      if (!nres.ok) throw new Error('No se pudo iniciar la firma.');
      const { nonce } = await nres.json();

      const message   = buildLoginMessage({ address, nonce });
      const provider  = await wallet.getEthereumProvider();
      const signature = await provider.request({ method: 'personal_sign', params: [message, address] });

      setStatus('linking');
      const res  = await fetch(`${API_URL}/api/auth/wallet/link`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ message, signature, address, nonce }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'No se pudo vincular la wallet.');

      updateUser(data.user || { walletAddress: address });
      setStatus('done');
    } catch (e) {
      setError(humanizeWalletError(e));
      setStatus('error');
    } finally {
      pending.current = false;
    }
  }, [updateUser]);

  useEffect(() => {
    if (!pending.current || !ready || !authenticated) return;
    const wallet = pickWallet(wallets);
    if (!wallet) return;
    runLink(wallet);
  }, [ready, authenticated, wallets, runLink]);

  const start = useCallback(() => {
    setError('');
    setStatus('connecting');
    pending.current = true;
    const wallet = pickWallet(wallets);
    if (authenticated && wallet) runLink(wallet);
    else openPrivy();
  }, [authenticated, wallets, openPrivy, runLink]);

  // Ya tiene wallet vinculada → no mostrar nada.
  if (user?.walletAddress) return null;

  const busy  = status === 'connecting' || status === 'signing' || status === 'linking';
  const label = status === 'signing'   ? 'Firmá en tu wallet…'
              : status === 'linking'   ? 'Vinculando…'
              : status === 'connecting'? 'Conectando…'
              : 'Generar mi wallet';

  if (status === 'done') {
    return (
      <div className="wl wl-done">
        <FiCheckCircle className="wl-done-ic" />
        <span>¡Wallet lista! 🔑 Ahora tu plata vive en tu wallet — tu llave, tu control. Entrás con email <strong>o</strong> con tu wallet, misma cuenta y reputación.</span>
      </div>
    );
  }

  return (
    <div className="wl wl-hero">
      <div className="wl-head">
        <span className="wl-ic"><FaWallet /></span>
        <div>
          <h3 className="wl-title">🔑 Generá tu wallet — tu plata, tu llave</h3>
          <p className="wl-sub">
            Tu cuenta todavía <strong>no tiene wallet</strong>. Generá una <strong>en segundos</strong> (sin semillas ni apps):
            así tu plata es <strong>tuya de verdad</strong>, con autocustodia. No perdés nada — conservás tu usuario, reputación e historial.
          </p>
        </div>
      </div>

      <button type="button" className="wl-btn" onClick={start} disabled={!ready || busy}>
        <FaWallet /> {busy ? label : <>{label} <FiArrowRight /></>}
      </button>

      {error && <div className="wl-error">{error}</div>}

      <ul className="wl-perks">
        <li>🔒 Tu plata vive en tu wallet, no en Bitplify (nadie la toca)</li>
        <li>⚡ Sin contraseñas ni semillas: entrás con tu llave</li>
        <li>🛟 Recuperación con huella/Face ID + gente de confianza</li>
      </ul>
    </div>
  );
}
